/* ============================================================
 * class ExposureView
 *
 * Example
 * ------------------------------------------------------------
 * new ExposureView(
 *   document.querySelector('xxx'),
 *   {
 *     callback: () => {
 *        // Do something...
 *     },
 *     duration: 3000,
 *   },
 *   {
 *     rootMargin: '-25% 0%',
 *   },
 * );
 *
 * Attributes
 * ------------------------------------------------------------
 * - element: Required, Element that need to be exposed
 * - options.callback: Required, Callback event
 * - options.duration: Not required, Exposure duration, The callback will only be triggered after options.duration.
 * - options.once: Not required, The callback is triggered only once.
 * - options.contain: Not required, check the Exposure element is in the rootBounds,
 *  you can be used when the element height is close to the rootBounds height or when the element height may exceed the rootBounds height.
 * - observeOptions: Not required, see: https://developer.mozilla.org/en-US/docs/Web/API/IntersectionObserver/IntersectionObserver
 * ============================================================ */
class ExposureView {
  constructor(element, options = {}, observeOptions = {}) {
    this.element = element;
    this.isIntersecting = false;
    this.timer = null;
    this.observer = null;
    this.exposureStartTime = 0;
    this.finished = false;
    this.entrieData = null;
    this.options = Object.assign(
      {
        callback: null,
        duration: 0,
        once: true,
        contain: false,
      },
      options,
    );
    if (options.contain) {
      observeOptions.threshold = [...Array(100).keys()].map(
        (i) => (i + 1) / 100,
      );
    }
    this.observeOptions = observeOptions;
    if (!element || !options.callback || !('IntersectionObserver' in window)) {
      return;
    }
    window.requestAnimationFrame(() => {
      this._createObserve();
    });
  }

  _createObserve() {
    if (this.finished || this.observer) return;
    if ('IntersectionObserver' in window) {
      const observeOptions = { ...this.observeOptions };
      this.observer = new IntersectionObserver((entries) => {
        const entrieData = entries?.[0];
        this.entrieData = entrieData;
        this._isIntersectingChange(entrieData.isIntersecting);
      }, observeOptions);
      this.observer.observe(this.element);
    }
  }

  _isIntersectingChange(isIntersecting) {
    this.isIntersecting = isIntersecting;
    if (!this.isIntersecting) {
      this._reset();
      return;
    }
    this._onExposure();
  }

  _inRange() {
    const { contain } = this.options;
    if (!this.isIntersecting) {
      return false;
    }
    if (!contain) {
      return true;
    }
    if (!this.entrieData) {
      return false;
    }
    const { rootBounds, intersectionRatio, boundingClientRect } =
      this.entrieData;
    const height = boundingClientRect?.height;
    const rootBoundsHeight = rootBounds?.height;
    const halfRootBoundsHeight = rootBoundsHeight / 2;

    if (
      intersectionRatio >= 1 ||
      (height >= halfRootBoundsHeight &&
        height * intersectionRatio >= halfRootBoundsHeight)
    ) {
      return true;
    }

    return false;
  }

  _onExposure() {
    const { callback, duration, once } = this.options;
    if (this.finished || !this.isIntersecting) {
      return;
    }
    if (!this._inRange()) {
      this._isIntersectingChange(false);
      return;
    }
    if (duration > 0) {
      const now = Date.now();
      if (!this.exposureStartTime) {
        this.exposureStartTime = now;
        this._createTimer();
        return;
      }
      const needRunCallback = now - this.exposureStartTime >= duration;
      if (needRunCallback) {
        callback();
        if (once) {
          this._destroy();
          return;
        }
        this._isIntersectingChange(false);
      }
      return;
    }
    callback();
    if (once) {
      this._destroy();
    }
  }
  _createTimer() {
    this._clearTimer();
    this.timer = window.setTimeout(() => {
      this._onExposure();
    }, this.options.duration);
  }

  _clearTimer() {
    if (this.timer) {
      window.clearTimeout(this.timer);
      this.timer = null;
    }
  }

  _reset() {
    this.exposureStartTime = 0;
    this._clearTimer();
  }

  _destroy() {
    this.finished = true;
    this.observer?.disconnect();
    this._reset();
  }
}

export default ExposureView;
